import { useEffect, useRef } from 'react';
import { handleSessionExpired, refreshToken } from '@/lib/api/tokenRefreshService';
import { API_CONFIG } from '@/config/api';

/**
 * Minimum gap between two proactive refreshes. The `visibilitychange` and
 * `focus` events both tend to fire when a user returns to a backgrounded tab,
 * so this collapses that pair (and any other rapid triggers) into a single
 * call. `refreshToken()` already dedupes concurrent in-flight requests; this
 * additionally avoids back-to-back sequential ones.
 */
const MIN_REFRESH_GAP_MS = 60_000;

/** Refresh this long before the session actually expires. */
const REFRESH_LEAD_MS = 60_000;

/**
 * Floor for the expiry-derived delay, so a near-expiry (or already-expired)
 * session can't spin the timer faster than once a minute.
 */
const MIN_INTERVAL_MS = 60_000;

/**
 * Keep the shared httpOnly `e6_session` cookie fresh while the user is signed
 * in, so the session never silently expires under them.
 *
 * Why this exists (PLT-8655): the Pipelines page is a federated remote
 * (`consoleApp`) that makes its own (cookie-authenticated) API calls. When the
 * session JWT in the cookie expires, Envoy rejects those calls with the raw
 * "Jwt is expired" error. Because the cookie is same-origin and shared by host
 * and remote, refreshing it from the host keeps the remote's calls
 * authenticated too — no token has to cross the federation boundary.
 *
 * Scheduling: a self-rescheduling timer fires ~one minute before the session
 * expires. The first delay uses `sessionExpiresAtMs` (from compute-api's /me);
 * after that, each `/auth/refresh` response carries the new token's `expiresAt`,
 * so the next refresh is sized straight off it — the cadence tracks each token
 * with no extra /me round-trip. When no expiry is known it falls back to the
 * configured `API_CONFIG.sessionRefreshIntervalMs`. It also refreshes when the
 * tab regains visibility or focus; a hidden tab isn't refreshed on the timer —
 * the visibility handler covers its return. On a genuine 401 from the refresh
 * endpoint (session truly gone) it escalates to the existing session-expired
 * flow (overlay + login redirect); transient failures (5xx, network) are left
 * for the next tick.
 *
 * @param enabled - only schedule refreshes while the user is authenticated.
 * @param sessionExpiresAtMs - epoch ms when the session expires (from /me), or
 *   null/undefined when unknown (then the configured interval is used). Only
 *   sizes the FIRST delay; later delays come from the refresh response.
 */
export function useProactiveTokenRefresh(
  enabled: boolean,
  sessionExpiresAtMs?: number | null
): void {
  // Far in the past so the very first trigger is never throttled (real
  // Date.now() is a large epoch; the sentinel keeps tests starting at t=0
  // honest too).
  const lastRefreshAtRef = useRef<number>(Number.NEGATIVE_INFINITY);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    let timerId: number | undefined;
    // Latest known expiry (epoch ms): seeded from /me, then updated from each
    // refresh response's expiresAt.
    let currentExpiryMs = sessionExpiresAtMs ?? null;

    // Delay until the next refresh: ~one minute before expiry, floored so a
    // near- or past-expiry value can't spin the timer too fast; the configured
    // interval is the fallback when no expiry is known.
    const nextDelay = (): number =>
      currentExpiryMs != null && Number.isFinite(currentExpiryMs)
        ? Math.max(MIN_INTERVAL_MS, currentExpiryMs - Date.now() - REFRESH_LEAD_MS)
        : API_CONFIG.sessionRefreshIntervalMs;

    const runRefresh = async (): Promise<void> => {
      const now = Date.now();
      if (now - lastRefreshAtRef.current < MIN_REFRESH_GAP_MS) {
        return;
      }
      lastRefreshAtRef.current = now;

      const result = await refreshToken();
      if (cancelled) {
        return;
      }
      if (!result.success && result.status === 401) {
        // Session genuinely gone — hand off to the existing session-expired
        // flow. Don't escalate on a transient 5xx or network error (no status).
        handleSessionExpired(result.logoutUrl);
        return;
      }
      if (result.success && result.expiresAt) {
        // Size the next refresh off the freshly-issued token.
        const parsed = Date.parse(result.expiresAt);
        if (Number.isFinite(parsed)) {
          currentExpiryMs = parsed;
        }
      }
    };

    function scheduleNext(): void {
      timerId = window.setTimeout(async () => {
        if (document.visibilityState === 'visible') {
          await runRefresh();
        }
        if (cancelled) {
          return;
        }
        scheduleNext();
      }, nextDelay());
    }

    const refreshOnWake = (): void => {
      if (document.visibilityState === 'visible') {
        void runRefresh();
      }
    };

    scheduleNext();
    document.addEventListener('visibilitychange', refreshOnWake);
    window.addEventListener('focus', refreshOnWake);

    return () => {
      cancelled = true;
      if (timerId !== undefined) {
        window.clearTimeout(timerId);
      }
      document.removeEventListener('visibilitychange', refreshOnWake);
      window.removeEventListener('focus', refreshOnWake);
    };
  }, [enabled, sessionExpiresAtMs]);
}
